import { useId, useState } from "react";
import { Button } from "../../design-v2";
import MetricBar from "./MetricBar";
import { formatSetCount } from "../../features/insights/domain/insightsPresentation";

export default function MuscleDistribution({
  items = [],
  expanded,
  onExpandedChange,
  title = "Muscle Distribution",
  // Additive, opt-in. Defaults to true, which is the existing behaviour for
  // every consumer (Cycle Viewer, Home, /program): an internal "Show all /
  // Show less" control that hides zero-set muscles until it is pressed.
  // Passing false renders every canonical muscle row up front and omits the
  // control entirely -- used by the builders, whose own outer collapse already
  // provides the disclosure.
  showAllControl = true,
  // Additive, opt-in. `weighted` reads `item.weightedSets` (canonical
  // DIRECT = 1, INDIRECT = 0.5, MINOR = 0) instead of `item.rawSets` and labels
  // the values as weighted. `unclassifiedSets` > 0 adds a discreet note that
  // some sets have no contribution metadata and are not counted. `status`
  // "loading" / "error" replaces the rows while the contribution map loads.
  weighted = false,
  unclassifiedSets = 0,
  status = "ready",
}) {
  const [internalShowAll, setInternalShowAll] = useState(false);
  const isControlled = typeof expanded === "boolean";
  const showAll = showAllControl ? (isControlled ? expanded : internalShowAll) : true;
  const gridId = `lz-product-muscle-distribution-${useId().replace(/:/g, "")}`;
  const valueOf = (item) => Number(weighted ? item?.weightedSets : item?.rawSets);
  const canonicalItems = status === "ready"
    ? items.filter((item) => Number.isFinite(valueOf(item)))
    : [];
  const visibleItems = showAll
    ? canonicalItems
    : canonicalItems.filter((item) => valueOf(item) > 0);
  const maxSets = Math.max(1, ...canonicalItems.map((item) => valueOf(item) || 0));
  const unclassified = Number(unclassifiedSets) || 0;
  const emptyMessage = status === "loading"
    ? "Loading muscle volume…"
    : status === "error"
      ? "Muscle volume is unavailable right now."
      : "No targeted strength sets.";

  return (
    <section
      className="lz-product-muscle-distribution"
      aria-label={title}
    >
      <div className="lz-product-muscle-distribution__header">
        <h2 className="lz-product-section-title">{title}</h2>
        {showAllControl && canonicalItems.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="lz-product-muscle-distribution__toggle"
            aria-expanded={showAll}
            aria-controls={gridId}
            onClick={() => {
              const nextValue = !showAll;
              if (!isControlled) {
                setInternalShowAll(nextValue);
              }
              onExpandedChange?.(nextValue);
            }}
          >
            <span>{showAll ? "Show less" : "Show all"}</span>
            <svg
              className="lz-product-muscle-distribution__chevron"
              data-testid="muscle-distribution-chevron"
              viewBox="0 0 16 16"
              aria-hidden="true"
            >
              <path d="M3.5 5.75 8 10.25l4.5-4.5" />
            </svg>
          </Button>
        ) : null}
      </div>
      {visibleItems.length > 0 ? (
        <div
          className="lz-product-muscle-distribution__grid"
          id={gridId}
          data-expanded={showAll ? "true" : "false"}
        >
          {visibleItems.map((item) => {
            const value = valueOf(item);
            const unit = value === 1 ? "set" : "sets";
            return (
              <div className="lz-product-muscle-distribution__item" key={item.key || item.label}>
                <div className="lz-product-muscle-distribution__copy">
                  <span>{item.label}</span>
                  <span>
                    {weighted ? `${formatSetCount(value)} weighted ${unit}` : `${item.rawSets} ${unit}`}
                  </span>
                </div>
                <MetricBar
                  value={value}
                  max={maxSets}
                  label={`${item.label} muscle distribution`}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="lz-product-muscle-distribution__empty">{emptyMessage}</p>
      )}
      {weighted && status === "ready" && canonicalItems.length > 0 ? (
        <p className="lz-product-muscle-distribution__empty">
          Weighted sets: direct = 1, indirect = 0.5.
        </p>
      ) : null}
      {weighted && status === "ready" && unclassified > 0 ? (
        <p className="lz-product-muscle-distribution__empty" data-testid="muscle-distribution-unclassified">
          {formatSetCount(unclassified)} {unclassified === 1 ? "set" : "sets"} not counted: muscle data unavailable.
        </p>
      ) : null}
    </section>
  );
}
